import { useState, useEffect, useRef } from "react";
import { C } from "../constants";
import { Ic } from "./ui/Icons";
import Sidebar from "./Sidebar";
import Home from "../modules/Home";
import Account from "../modules/Account";
import ProjectRequest from "../modules/projects/ProjectRequest";
import QuoteManagement from "../modules/quotes/QuoteManagement";
import OrderTracking from "../modules/orders/OrderTracking";

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export default function Portal({ user, onLogout, onUpdate }) {
  const [section, setSection] = useState(() => localStorage.getItem("boal_section") || "home");
  const [collapsed, setCollapsed] = useState(false);
  const [pendingNav, setPendingNav] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [projectKey, setProjectKey] = useState(0);

  // Keep a stable ref to onLogout so the idle timer never captures a stale closure
  const onLogoutRef = useRef(onLogout);
  useEffect(() => { onLogoutRef.current = onLogout; }, [onLogout]);

  // ─── Idle auto-logout ────────────────────────────────────────
  useEffect(() => {
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => onLogoutRef.current(), IDLE_TIMEOUT_MS);
    };
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset(); // start the timer immediately on mount
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const go = id => {
    if (id === "logout") { onLogout(); return; }
    if (hasUnsavedChanges && id !== section) { setPendingNav(id); return; }
    if (id === "projects" && hasUnsavedChanges) { setPendingNav("projects-reset"); return; }
    if (id === "projects") setProjectKey(k => k + 1);
    setSection(id);
    localStorage.setItem("boal_section", id);
    setHasUnsavedChanges(false);
  };

  const confirmNav = () => {
    if (pendingNav === "projects-reset") { setProjectKey(k => k + 1); setPendingNav(null); setHasUnsavedChanges(false); }
    else if (pendingNav) { setSection(pendingNav); localStorage.setItem("boal_section", pendingNav); setPendingNav(null); setHasUnsavedChanges(false); }
  };

  const cancelNav = () => setPendingNav(null);

  const titles = {
    home: "Dashboard", projects: "Project & Quote Request",
    quotes: "Quote Management", orders: "Order Status & Tracking", account: "My Account",
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: C.grayBg, fontFamily: "'DM Sans','Segoe UI',system-ui,sans-serif" }}>
      <Sidebar section={section} go={go} collapsed={collapsed} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <header style={{ height: 64, background: C.white, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 28px", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <button onClick={() => setCollapsed(!collapsed)} style={{ background: "none", border: "none", cursor: "pointer", color: C.t3, display: "flex", padding: 4 }}><Ic.Menu /></button>
            <h2 style={{ fontSize: 16, fontWeight: 600, color: C.t1, margin: 0 }}>{titles[section]}</h2>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 13, color: C.t2 }}>{user.name}</span>
            <button onClick={() => go("account")} style={{ width: 34, height: 34, borderRadius: 10, background: C.blueLight, display: "flex", alignItems: "center", justifyContent: "center", border: "none", cursor: "pointer", color: C.navy, fontWeight: 700, fontSize: 13 }}
              onMouseEnter={e => e.currentTarget.style.background = `${C.blue}20`} onMouseLeave={e => e.currentTarget.style.background = C.blueLight}>
              {user.name.split(" ").map(n => n[0]).join("")}
            </button>
          </div>
        </header>
        <main style={{ flex: 1, padding: "32px 28px", overflowY: "auto" }}>
          {section === "home"     && <Home user={user} go={go} />}
          {section === "projects" && <ProjectRequest key={projectKey} user={user} onUnsavedChange={setHasUnsavedChanges} />}
          {section === "quotes"   && <QuoteManagement user={user} />}
          {section === "orders"   && <OrderTracking user={user} />}
          {section === "account"  && <Account user={user} onUpdate={onUpdate} />}
        </main>
      </div>

      {pendingNav && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: C.white, borderRadius: 16, padding: "28px 32px", maxWidth: 400, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: C.t1, margin: "0 0 8px" }}>Unsaved Changes</h3>
            <p style={{ fontSize: 14, color: C.t2, margin: "0 0 24px", lineHeight: 1.5 }}>You have an unsubmitted request. Are you sure you want to leave? Your progress will be lost.</p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={cancelNav} style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.t2, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Stay</button>
              <button onClick={confirmNav} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: C.red, color: C.white, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Leave & Discard</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
